/**
 * Drift API Integration
 *
 * Fetches dependency drift data from the backend and transforms it
 * to the frontend DriftReport format used by UI components.
 */

import { api } from '../../../services/authenticatedApi';
import { logger } from '../../../utils/logger';
import type {
  DriftReport,
  DriftGrade,
  AgeBucket,
  StaleDependency,
  DriftMetrics,
  DriftSecurity,
  DriftInterpretation,
} from '../../types/analysis.types';

/**
 * Backend API response types (matches server/types/dependencyDrift.ts)
 */
interface BackendAgeBucket {
  label: string;
  display: string;
  count: number;
  severity: 'critical' | 'high' | 'medium' | 'low';
  examples: string[];
}

interface BackendDriftItem {
  name: string;
  ecosystem: string;
  currentVersion: string;
  latestVersion: string;
  isOutdated: boolean;
  versionsBehind: number;
  ageYears: number;
  ageBucket: string;
  isDev: boolean;
  error?: string;
}

interface BackendSecurityExposure {
  criticalAge: number;
  warningAge: number;
  criticalDeps: string[];
  warningDeps: string[];
}

interface BackendDriftReport {
  totalDependencies: number;
  outdatedCount: number;
  upToDateCount: number;
  unknownCount: number;
  driftRatio: number;
  score: number;
  grade: 'A' | 'B' | 'C' | 'D' | 'F';
  ageDistribution: BackendAgeBucket[];
  mostStale: BackendDriftItem[];
  byEcosystem: Record<
    string,
    { total: number; outdated: number; upToDate: number; unknown: number }
  >;
  securityExposure?: BackendSecurityExposure;
  analyzedAt: string;
  projects?: Array<{ project_index: number; score: number; grade: string; skipped?: boolean }>;
  error?: string;
}

/**
 * Map backend age bucket labels to frontend bucket format
 * Backend labels: "over7years", "4to6years", "2to4years", "under2years"
 * Frontend labels: "7+", "4-6", "2-4", "<2"
 */
function mapAgeBucket(label: string): '7+' | '4-6' | '2-4' | '<2' {
  const labelLower = label.toLowerCase();
  if (labelLower.includes('over7') || labelLower === '7+') return '7+';
  if (labelLower.includes('4to6') || labelLower === '4-6') return '4-6';
  if (labelLower.includes('under') || labelLower === '<2') return '<2';
  if (labelLower.includes('2to4') || labelLower === '2-4') return '2-4';
  return '<2';
}

/**
 * Calculate security risk level based on age distribution
 */
function calculateRiskLevel(
  security?: BackendSecurityExposure,
): 'low' | 'medium' | 'high' | 'critical' {
  if (!security) return 'low';

  const { criticalAge, warningAge } = security;
  if (criticalAge > 10) return 'critical';
  if (criticalAge > 5 || warningAge > 15) return 'high';
  if (warningAge > 5) return 'medium';
  return 'low';
}

/**
 * Get recommended security scan tools based on primary language.
 * Language-agnostic: covers major ecosystems and their package managers.
 */
function getScanToolsForLanguage(language: string): string[] {
  const lang = language.toLowerCase();

  // Python ecosystem (pip, uv, poetry, conda)
  if (lang === 'python') {
    return ['pip-audit', 'safety', 'bandit', 'uv pip audit'];
  }

  // JavaScript/TypeScript ecosystem (npm, pnpm, yarn, bun)
  if (lang === 'javascript' || lang === 'typescript') {
    return ['npm audit', 'pnpm audit', 'yarn audit', 'snyk'];
  }

  // Java/Kotlin ecosystem (maven, gradle)
  if (lang === 'java' || lang === 'kotlin') {
    return [
      'OWASP Dependency-Check',
      'gradle dependencyCheckAnalyze',
      'mvn dependency-check:check',
      'Snyk',
    ];
  }

  // Go ecosystem
  if (lang === 'go' || lang === 'golang') {
    return ['govulncheck', 'nancy', 'Snyk'];
  }

  // Ruby ecosystem (bundler, gem)
  if (lang === 'ruby') {
    return ['bundler-audit', 'brakeman', 'Snyk'];
  }

  // Rust ecosystem (cargo)
  if (lang === 'rust') {
    return ['cargo-audit', 'cargo-deny', 'Trivy'];
  }

  // PHP ecosystem (composer)
  if (lang === 'php') {
    return ['composer audit', 'local-php-security-checker', 'Snyk'];
  }

  // .NET ecosystem (nuget, dotnet)
  if (lang === 'c#' || lang === 'csharp' || lang === '.net' || lang === 'f#') {
    return ['dotnet list package --vulnerable', 'Snyk', 'OWASP Dependency-Check'];
  }

  // Swift/iOS ecosystem
  if (lang === 'swift' || lang === 'objective-c') {
    return ['swift package audit', 'Snyk', 'CocoaPods audit'];
  }

  // Scala ecosystem (sbt, maven)
  if (lang === 'scala') {
    return ['sbt-dependency-check', 'OWASP Dependency-Check', 'Snyk'];
  }

  // Elixir ecosystem (mix, hex)
  if (lang === 'elixir') {
    return ['mix_audit', 'Sobelow', 'Snyk'];
  }

  // C/C++ ecosystem
  if (lang === 'c' || lang === 'c++' || lang === 'cpp') {
    return ['Trivy', 'Grype', 'OWASP Dependency-Check'];
  }

  // Default - universal tools that work across ecosystems
  return ['Snyk', 'Trivy', 'Grype', 'OWASP Dependency-Check'];
}

/**
 * Transform backend DependencyDriftReport to frontend DriftReport
 */
export function transformDriftReport(
  backend: BackendDriftReport,
  repositoryName: string = 'Unknown',
  projectType: string = 'unknown',
  primaryLanguage: string = 'Unknown',
): DriftReport {
  // Transform age distribution
  const ageDistribution: AgeBucket[] = backend.ageDistribution.map((bucket) => ({
    bucket: mapAgeBucket(bucket.label),
    count: bucket.count,
    examples: bucket.examples,
  }));

  // Ensure all buckets exist (UI expects all 4)
  const bucketOrder: Array<'7+' | '4-6' | '2-4' | '<2'> = ['7+', '4-6', '2-4', '<2'];
  const filledDistribution: AgeBucket[] = bucketOrder.map((bucket) => {
    const existing = ageDistribution.find((d) => d.bucket === bucket);
    return existing || { bucket, count: 0, examples: [] };
  });

  // Transform stale dependencies
  const staleDependencies: StaleDependency[] = backend.mostStale.map((dep) => ({
    name: dep.name,
    pinned: dep.currentVersion,
    latest: dep.latestVersion,
    ageYears: dep.ageYears,
    type: dep.isDev ? 'dev' : 'direct',
  }));

  // Calculate oldest pinned years from most stale
  const oldestPinnedYears =
    backend.mostStale.length > 0 ? Math.max(...backend.mostStale.map((d) => d.ageYears)) : 0;

  // Calculate share older than 2 and 4 years
  const totalKnown = backend.totalDependencies - backend.unknownCount;
  const over2Years = filledDistribution
    .filter((b) => b.bucket === '7+' || b.bucket === '4-6' || b.bucket === '2-4')
    .reduce((sum, b) => sum + b.count, 0);
  const over4Years = filledDistribution
    .filter((b) => b.bucket === '7+' || b.bucket === '4-6')
    .reduce((sum, b) => sum + b.count, 0);

  const metrics: DriftMetrics = {
    totalDependencies: backend.totalDependencies,
    outdatedDependencies: backend.outdatedCount,
    driftRatio: backend.driftRatio,
    oldestPinnedYears,
    shareOlderThan2Years: totalKnown > 0 ? over2Years / totalKnown : 0,
    shareOlderThan4Years: totalKnown > 0 ? over4Years / totalKnown : 0,
  };

  const security: DriftSecurity = {
    depsOlderThan12Mo: backend.securityExposure?.warningAge || 0,
    depsOlderThan24Mo: backend.securityExposure?.criticalAge || 0,
    riskLevel: calculateRiskLevel(backend.securityExposure),
    scanTools: getScanToolsForLanguage(primaryLanguage),
  };

  // Generate interpretation (backend doesn't provide this, so generate based on score)
  const interpretation: DriftInterpretation = generateInterpretation(
    backend.score,
    backend.grade,
    backend.outdatedCount,
    backend.totalDependencies,
    backend.driftRatio,
  );

  const projects = backend.projects
    ?.filter((p) => !p.skipped)
    .map((p) => ({
      project_index: p.project_index,
      score: p.score,
      grade: p.grade as DriftGrade,
    }));

  return {
    analysisDate: backend.analyzedAt,
    repositoryName,
    projectType,
    primaryLanguage,
    score: backend.score,
    grade: backend.grade,
    metrics,
    ageDistribution: filledDistribution,
    staleDependencies,
    security,
    interpretation,
    ...(projects && projects.length > 0 ? { projects } : {}),
  };
}

/**
 * Generate interpretation text based on drift metrics
 */
function generateInterpretation(
  _score: number,
  grade: string,
  outdated: number,
  total: number,
  driftRatio: number,
): DriftInterpretation {
  const pct = Math.round(driftRatio * 100);

  let summary: string;
  let bottomLine: string;
  let riskImplications: string;

  if (grade === 'A') {
    summary = 'Dependencies are well-maintained with minimal drift.';
    bottomLine = `Only ${outdated} of ${total} dependencies are behind latest versions. Current maintenance practices are effective.`;
    riskImplications =
      'Low drift keeps upgrade complexity manageable and minimizes security exposure.';
  } else if (grade === 'B') {
    summary = 'Good dependency hygiene with some room for improvement.';
    bottomLine = `${outdated} of ${total} dependencies (${pct}%) are outdated. Consider scheduling regular update cycles.`;
    riskImplications =
      'Minor drift accumulation. Regular updates will prevent technical debt buildup.';
  } else if (grade === 'C') {
    summary = 'Moderate dependency drift requiring attention.';
    bottomLine = `${outdated} of ${total} dependencies (${pct}%) are behind latest versions. Schedule updates for core dependencies.`;
    riskImplications =
      'Moderate drift may complicate future updates. Consider quarterly dependency reviews.';
  } else if (grade === 'D') {
    summary = 'Significant dependency drift detected.';
    bottomLine = `${outdated} of ${total} dependencies (${pct}%) are outdated. Incremental updates recommended to reduce risk.`;
    riskImplications =
      'High drift increases upgrade complexity and expands security exposure windows.';
  } else {
    summary = 'Critical dependency drift requiring immediate attention.';
    bottomLine = `${outdated} of ${total} dependencies (${pct}%) are severely outdated. Prioritize security-critical updates.`;
    riskImplications =
      'Critical drift significantly increases security risk and upgrade complexity. Immediate action recommended.';
  }

  return { summary, bottomLine, riskImplications };
}

/**
 * Fetch drift report for a repository
 *
 * @param repositoryId - UUID of the repository
 * @param repositoryName - Name for display purposes
 * @param projectType - Project type for display
 * @param primaryLanguage - Primary language for display
 * @returns Transformed DriftReport or null if not found
 */
export async function fetchDriftReport(
  repositoryId: string,
  repositoryName?: string,
  projectType?: string,
  primaryLanguage?: string,
): Promise<DriftReport | null> {
  try {
    const response = await api.get<BackendDriftReport>(
      `/repositories/${repositoryId}/dependency-drift`,
    );
    return transformDriftReport(response.data, repositoryName, projectType, primaryLanguage);
  } catch (error: unknown) {
    const axiosErr = error as { response?: { status?: number } };
    if (axiosErr.response?.status === 404) {
      // No drift analysis available yet
      return null;
    }
    logger.error('Failed to fetch drift report', { error });
    throw error;
  }
}
